import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, X, Package, Calendar, Tag, Sparkles, SlidersHorizontal, Download, FileText, Loader2, Check, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import LabelDusSheetGrid, { SingleLabelSVG } from '@/Components/production/LabelDusSheetGrid';
import { LOGO_HITAM_SDE_BASE64 } from '@/Components/production/logoHitamBase64';
import SuratJalanDocument from '@/Components/production/SuratJalanDocument';
import { generateSuratJalanWithLabelsPdf, directPrintSuratJalanWithLabelsPdf } from '@/lib/suratJalanPdfGenerator';
import { calculateMixBoxAllocation } from '@/utils/dusAllocationHelper';

// Format helper
const formatDateIndo = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function LabelDusPrintModal({
  isOpen,
  onClose,
  order,
  shipment,
  initialSettings = {},
  initialMode = 'labels',
  allTasks = []
}) {
  const suratJalanRef = useRef(null);
  const sheetRefs = useRef([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState(initialMode === 'both' ? 'all' : 'labels');

  // Available variants from order / shipment
  const availableVariants = useMemo(() => {
    if (shipment?.items?.length > 0) {
      return shipment.items.map(it => ({
        name: (it.variant_name || it.product_name || 'Produk Utama').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim(),
        qty: Number(it.qty_akhir) || Number(it.qty_dikirim) || Number(it.qty) || 0,
        catatan: it.catatan_label || it.catatan || '',
        pcs_per_dus: Number(it.pcs_per_dus) || undefined,
        sisa_action: it.sisa_action || 'pisah'
      }));
    }
    if (order?.products?.length > 0) {
      return order.products.map(p => ({
        name: (p.product_name || p.variant_name || 'Produk Utama').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim(),
        qty: Number(p.qty) || Number(p.target_qty) || 0,
        catatan: ''
      }));
    }
    return [{
      name: order?.product_name || order?.brand_name || 'Produk Utama',
      qty: Number(order?.target_qty) || Number(order?.final_qty) || 0,
      catatan: ''
    }];
  }, [order, shipment]);

  const totalAllQty = useMemo(() => {
    return availableVariants.reduce((sum, v) => sum + (Number(v.qty) || 0), 0);
  }, [availableVariants]);

  // Selected Variant ('all' or specific variant name)
  const [selectedVariantName, setSelectedVariantName] = useState(() => {
    if (availableVariants.length > 1) {
      if (!initialSettings.variant_name || initialSettings.variant_name === 'all') {
        return 'all';
      }
      return initialSettings.variant_name;
    }
    return availableVariants[0]?.name || 'all';
  });

  const currentVariant = useMemo(() => {
    if (selectedVariantName === 'all') {
      return { name: 'Semua Varian', qty: totalAllQty };
    }
    if (selectedVariantName === 'mix_only') {
      return { name: 'Dus Campuran & Lebihan', qty: totalAllQty };
    }
    return availableVariants.find(v => v.name === selectedVariantName) || availableVariants[0] || { name: 'Produk', qty: 100 };
  }, [availableVariants, selectedVariantName, totalAllQty]);

  // Label Size Option: 'besar' (7 x 7.5 cm, 8/sheet) vs 'kecil' (5.42 x 5.8 cm, 15/sheet)
  const [labelSize, setLabelSize] = useState(initialSettings.label_size || 'besar');

  // Pcs per dus
  const [pcsPerDus, setPcsPerDus] = useState(
    initialSettings.pcs_per_dus || (currentVariant.qty > 100 ? 100 : currentVariant.qty || 50)
  );

  // Custom total pcs
  const [totalPcs, setTotalPcs] = useState(currentVariant.qty || 100);

  // Deteksi varian-varian yang memiliki sisa (remainder > 0)
  const variantsWithRemainder = useMemo(() => {
    const p = Math.max(1, Number(pcsPerDus) || Number(initialSettings.pcs_per_dus) || 25);
    return availableVariants.filter(v => {
      const vQty = Math.max(0, Number(v.qty) || 0);
      const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
      return (vQty % boxCap) > 0;
    });
  }, [availableVariants, pcsPerDus, initialSettings.pcs_per_dus]);

  // Hanya varian yang secara eksplisit diset 'gabung' dan memiliki sisa
  const variantsToGabung = useMemo(() => {
    const p = Math.max(1, Number(pcsPerDus) || Number(initialSettings.pcs_per_dus) || 25);
    return availableVariants.filter(v => {
      const vQty = Math.max(0, Number(v.qty) || 0);
      const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
      return v.sisa_action === 'gabung' && (vQty % boxCap) > 0;
    });
  }, [availableVariants, pcsPerDus, initialSettings.pcs_per_dus]);

  const hasMultipleRemainders = variantsWithRemainder.length > 1;
  const hasMultipleGabung = variantsToGabung.length > 1;

  // Toggle apakah sisa varian digabung jadi dus campuran:
  // HANYA aktif jika ada >= 2 item bertanda 'gabung'! Jika user pilih 'pisah' (kardus tersendiri), JANGAN dicampur!
  const [useGabungSisa, setUseGabungSisa] = useState(() => {
    return hasMultipleGabung;
  });

  useEffect(() => {
    setUseGabungSisa(hasMultipleGabung);
  }, [hasMultipleGabung]);

  const hasMixBoxes = hasMultipleGabung && useGabungSisa;

  // Sync totalPcs when variant changes
  const handleVariantChange = (vName) => {
    setSelectedVariantName(vName);
    if (vName === 'all' || vName === 'mix_only') {
      setTotalPcs(totalAllQty);
    } else {
      const v = availableVariants.find(item => item.name === vName);
      if (v) {
        setTotalPcs(v.qty);
        const vCap = Number(v.pcs_per_dus);
        if (vCap) setPcsPerDus(vCap);
      }
    }
  };

  // Compute total boxes
  const totalBoxes = useMemo(() => {
    const p = Math.max(1, Number(pcsPerDus) || 1);
    const tot = Math.max(1, Number(totalPcs) || 1);
    return Math.ceil(tot / p);
  }, [pcsPerDus, totalPcs]);

  // Default Tanggal Produksi calculation
  const defaultTglProduksi = useMemo(() => {
    if (initialSettings.tgl_produksi) return initialSettings.tgl_produksi;

    // Prioritas 1: SPK printed date
    if (order?.spk_printed_date || order?.spk_date) {
      const d = order.spk_printed_date || order.spk_date;
      return d.split('T')[0];
    }
    // Prioritas 2: Mulai diracik / mixing task started_at
    const mixTask = allTasks?.find(t =>
      t.po_id === order?.id &&
      (t.task_name?.toLowerCase().includes('racik') ||
       t.task_name?.toLowerCase().includes('mix') ||
       t.role?.toLowerCase().includes('racik'))
    );
    if (mixTask?.started_at) {
      return mixTask.started_at.split('T')[0];
    }
    // Prioritas 3: H-2 dari Tanggal Pengiriman / Hari ini
    const shipDate = shipment?.shipped_date || order?.shipped_date ? new Date(shipment?.shipped_date || order?.shipped_date) : new Date();
    shipDate.setDate(shipDate.getDate() - 2);
    return shipDate.toISOString().split('T')[0];
  }, [initialSettings.tgl_produksi, order, shipment, allTasks]);

  // Date States
  const [tglProduksi, setTglProduksi] = useState(defaultTglProduksi);

  // Exp Date (+3 years auto)
  const [expDate, setExpDate] = useState(() => {
    if (initialSettings.exp_date) return initialSettings.exp_date;
    try {
      const d = new Date(defaultTglProduksi);
      d.setFullYear(d.getFullYear() + 3);
      return d.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  });

  const handleTglProduksiChange = (val) => {
    setTglProduksi(val);
    try {
      const d = new Date(val);
      d.setFullYear(d.getFullYear() + 3);
      setExpDate(d.toISOString().split('T')[0]);
    } catch (e) {}
  };

  // Catatan Dus
  const [catatanDus, setCatatanDus] = useState(initialSettings.catatan_dus || '-');

  // Recipient info (Default ke Nama Brand jika kosong, jangan pernah pakai PIC)
  const recipientName = useMemo(() => {
    const brand = (order?.brand_name || order?.product_name || 'Brand').split(' / ')[0].trim();
    const raw = (shipment?.alamat_pengiriman?.penerima || shipment?.penerima || order?.alamat_pengiriman?.penerima || '').trim();
    if (!raw || raw === '-' || ['client', 'pic', 'asa', 'admin', brand.toLowerCase()].includes(raw.toLowerCase())) {
      return brand;
    }
    return raw;
  }, [shipment, order]);

  const recipientAddress = useMemo(() => {
    return (
      shipment?.alamat_pengiriman?.alamat_lengkap ||
      shipment?.alamat_lengkap ||
      order?.alamat_pengiriman?.alamat_lengkap ||
      order?.shipping_address ||
      order?.client_address ||
      'Alamat Pengiriman Brand'
    );
  }, [shipment, order]);

  const poNumber = useMemo(() => {
    return order?.order_number || order?.po_number || order?.invoice_id || 'PO-SATALA-001';
  }, [order]);

  const brandName = useMemo(() => {
    return (order?.brand_name || 'NAMA BRAND').toUpperCase();
  }, [order]);

  // Target Surat Jalan URL for QR Code
  const qrTargetUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sataladermatechessential.com';
    const targetId = order?.order_number || order?.po_number || order?.id || 'PO';
    return `${origin}/surat-jalan/${encodeURIComponent(targetId)}`;
  }, [order]);

  // Generate boxes array with variant & note preservation
  const boxesList = useMemo(() => {
    const p = Math.max(1, Number(pcsPerDus) || 1);
    const list = [];

    const isMultiGabung = useGabungSisa && variantsToGabung.length > 1;

    if (selectedVariantName === 'all' || selectedVariantName === 'mix_only') {
      if (isMultiGabung) {
        // --- MULTIPLE GABUNG SISA POOLING LOGIC ---
        const mixBoxCap = Math.max(1, Number(variantsToGabung[0]?.pcs_per_dus) || p || 25);
        const mappedItems = variantsToGabung.map(v => {
          const vQty = Math.max(0, Number(v.qty) || 0);
          const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
          return {
            name: v.name,
            remainder: vQty % boxCap
          };
        });

        const alloc = calculateMixBoxAllocation(mappedItems, mixBoxCap, shipment?.lebihan_variant);
        const { isOverflow, totalPooledRem, box1, box2 } = alloc;

        // Total boxes: full boxes + separate remainder boxes (for sisa_action !== 'gabung') + pooled boxes
        let totalFullBoxes = 0;
        let totalSeparateRemBoxes = 0;
        availableVariants.forEach(v => {
          const vQty = Math.max(0, Number(v.qty) || 0);
          const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
          const rem = vQty % boxCap;
          if (rem > 0) {
            totalFullBoxes += Math.floor(vQty / boxCap);
            if (v.sisa_action !== 'gabung') {
              totalSeparateRemBoxes += 1;
            }
          } else {
            totalFullBoxes += Math.ceil(vQty / boxCap);
          }
        });

        const pooledBoxesCount = (isOverflow && box2) ? 2 : (box1 && box1.total > 0 ? 1 : 0);
        const totalAllBoxes = totalFullBoxes + totalSeparateRemBoxes + pooledBoxesCount;

        let currentBoxNum = 1;

        // Step 1: Generate regular/full boxes for each variant + separate remainder if sisa_action !== 'gabung'
        availableVariants.forEach(v => {
          const vQty = Math.max(0, Number(v.qty) || 0);
          const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
          const rem = vQty % boxCap;
          const fullBoxesCount = Math.floor(vQty / boxCap);

          for (let b = 1; b <= fullBoxesCount; b++) {
            let qtyInThisBox = boxCap;
            let boxCatatan = v.catatan || catatanDus || '-';
            let boxVariant = v.name;

            // Only include regular boxes if printing all variants
            if (selectedVariantName === 'all') {
              list.push({
                boxIndex: currentBoxNum,
                boxNumberStr: `${String(currentBoxNum).padStart(2, '0')}/${String(totalAllBoxes).padStart(2, '0')}`,
                qtyPcs: qtyInThisBox,
                variantName: boxVariant,
                catatanDus: boxCatatan,
                isRemainder: false,
                isMixBox: false,
                sisaAction: v.sisa_action || 'pisah'
              });
            }
            currentBoxNum++;
          }

          // Separate remainder box for items that opted out of gabung
          if (v.sisa_action !== 'gabung' && rem > 0) {
            if (selectedVariantName === 'all') {
              list.push({
                boxIndex: currentBoxNum,
                boxNumberStr: `${String(currentBoxNum).padStart(2, '0')}/${String(totalAllBoxes).padStart(2, '0')}`,
                qtyPcs: rem,
                variantName: v.name,
                catatanDus: v.catatan ? `${v.catatan} · [Dus Sisa ${rem} pcs]` : `Dus Sisa (${rem} pcs)`,
                isRemainder: true,
                isMixBox: false,
                sisaAction: 'pisah'
              });
            }
            currentBoxNum++;
          }
        });

        // Step 2: Generate pooled remainder boxes
        if (isOverflow && box2) {
          const count1 = box1.items?.length || 2;
          const count2 = box2.items?.length || 2;
          const name1 = count1 > 1 ? `${count1} Variant Campuran` : (box1.items[0]?.name || 'Dus Campuran');
          const name2 = count2 > 1 ? `${count2} Variant Campuran` : (box2.items[0]?.name || 'Dus Lebihan');

          // 2.1 Dus Campuran (Full Mix - box1.total pcs)
          list.push({
            boxIndex: currentBoxNum,
            boxNumberStr: `${String(currentBoxNum).padStart(2, '0')}/${String(totalAllBoxes).padStart(2, '0')}`,
            qtyPcs: box1.total,
            variantName: name1,
            catatanDus: `Isi Campuran: ${box1.breakdownStr}`,
            breakdownItems: box1.items,
            isRemainder: true,
            isMixBox: true,
            sisaAction: 'gabung'
          });
          currentBoxNum++;

          // 2.2 Dus Lebihan (Sisa Campuran - box2.total pcs)
          list.push({
            boxIndex: currentBoxNum,
            boxNumberStr: `${String(currentBoxNum).padStart(2, '0')}/${String(totalAllBoxes).padStart(2, '0')}`,
            qtyPcs: box2.total,
            variantName: name2,
            catatanDus: `Isi Lebihan: ${box2.breakdownStr}`,
            breakdownItems: box2.items,
            isRemainder: true,
            isLebihanBox: true,
            sisaAction: 'gabung'
          });
          currentBoxNum++;
        } else if (box1 && box1.total > 0) {
          const count1 = box1.items?.length || 2;
          const name1 = count1 > 1 ? `${count1} Variant Campuran` : (box1.items[0]?.name || 'Dus Campuran');

          // Fits in 1 mixed box
          list.push({
            boxIndex: currentBoxNum,
            boxNumberStr: `${String(currentBoxNum).padStart(2, '0')}/${String(totalAllBoxes).padStart(2, '0')}`,
            qtyPcs: box1.total,
            variantName: name1,
            catatanDus: `Isi Campuran: ${box1.breakdownStr}`,
            breakdownItems: box1.items,
            isRemainder: true,
            isMixBox: true,
            sisaAction: 'gabung'
          });
          currentBoxNum++;
        }
      } else {
        // Regular logic for when not pooling multiple variants
        let totalAllBoxes = 0;
        availableVariants.forEach(v => {
          const vQty = Math.max(1, Number(v.qty) || 1);
          const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
          totalAllBoxes += Math.ceil(vQty / boxCap);
        });

        let currentBoxNum = 1;
        availableVariants.forEach(v => {
          const vQty = Math.max(1, Number(v.qty) || 1);
          const boxCap = Math.max(1, Number(v.pcs_per_dus) || p);
          const vBoxes = Math.ceil(vQty / boxCap);
          const remainder = vQty % boxCap;

          for (let b = 1; b <= vBoxes; b++) {
            let qtyInThisBox = boxCap;
            let boxCatatan = v.catatan || catatanDus || '-';
            let boxVariant = v.name;
            const isRemainderBox = b === vBoxes && remainder > 0;

            if (isRemainderBox) {
              qtyInThisBox = remainder;
              boxCatatan = v.catatan ? `${v.catatan} · [Dus Sisa ${remainder} pcs]` : `Dus Sisa (${remainder} pcs)`;
            }

            list.push({
              boxIndex: currentBoxNum,
              boxNumberStr: `${String(currentBoxNum).padStart(2, '0')}/${String(totalAllBoxes).padStart(2, '0')}`,
              qtyPcs: qtyInThisBox,
              variantName: boxVariant,
              catatanDus: boxCatatan,
              isRemainder: isRemainderBox,
              isMixBox: false,
              sisaAction: v.sisa_action || 'pisah'
            });
            currentBoxNum++;
          }
        });
      }
    } else {
      // Single specific variant
      const targetV = availableVariants.find(v => v.name === selectedVariantName) || availableVariants[0];
      const boxCap = Math.max(1, Number(targetV?.pcs_per_dus) || p);
      const vQty = Math.max(1, Number(totalPcs) || targetV?.qty || 1);
      const rem = vQty % boxCap;
      const numBoxes = Math.ceil(vQty / boxCap);

      for (let i = 1; i <= numBoxes; i++) {
        let qtyInThisBox = boxCap;
        let boxCatatan = targetV?.catatan || catatanDus || '-';
        let boxVariant = targetV?.name || 'Produk Utama';
        const isRemainderBox = (i === numBoxes && rem > 0);

        if (isRemainderBox) {
          qtyInThisBox = rem;
          boxCatatan = targetV?.catatan ? `${targetV.catatan} · [Dus Sisa ${rem} pcs]` : `Dus Sisa (${rem} pcs)`;
        }

        list.push({
          boxIndex: i,
          boxNumberStr: `${String(i).padStart(2, '0')}/${String(numBoxes).padStart(2, '0')}`,
          qtyPcs: qtyInThisBox,
          variantName: boxVariant,
          catatanDus: boxCatatan,
          isRemainder: isRemainderBox,
          isMixBox: false,
          sisaAction: targetV?.sisa_action || 'pisah'
        });
      }
    }
    return list;
  }, [selectedVariantName, availableVariants, pcsPerDus, totalPcs, catatanDus, shipment?.lebihan_variant, useGabungSisa, variantsWithRemainder, variantsToGabung]);

  // Chunk boxes into A4 Landscape Sheets (8 per sheet for Besar, 15 per sheet for Kecil)
  const sheets = useMemo(() => {
    const capacity = labelSize === 'besar' ? 8 : 15;
    const result = [];
    for (let i = 0; i < boxesList.length; i += capacity) {
      result.push({
        sheetIndex: result.length,
        boxes: boxesList.slice(i, i + capacity),
        sheetNumber: result.length + 1
      });
    }
    return result.length > 0 ? result : [{ sheetIndex: 0, boxes: [], sheetNumber: 1 }];
  }, [boxesList, labelSize]);

  // PDF Generation State
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfProgressText, setPdfProgressText] = useState('');

  // Handle Combined PDF Download (Page 1: Surat Jalan, Page 2+: Label Dus Landscape)
  const handleDownloadCombinedPdf = async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    setPdfProgressText('Menyiapkan dokumen...');

    try {
      const activeSheetEls = sheetRefs.current.filter(Boolean);
      await generateSuratJalanWithLabelsPdf({
        suratJalanElement: suratJalanRef.current,
        sheetElements: activeSheetEls,
        poNumber,
        includeSuratJalan: true,
        includeLabels: true,
        onProgress: (cur, tot, msg) => setPdfProgressText(msg)
      });
      toast.success('PDF Surat Jalan & Label Dus berhasil diunduh!');
    } catch (err) {
      console.error('Gagal membuat PDF gabungan:', err);
      toast.error('Gagal membuat PDF. Silakan coba lagi.');
    } finally {
      setIsGeneratingPdf(false);
      setPdfProgressText('');
    }
  };

  // Handle Download Label Dus Only
  const handleDownloadLabelsOnlyPdf = async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    setPdfProgressText('Menyiapkan lembaran label...');

    try {
      const activeSheetEls = sheetRefs.current.filter(Boolean);
      await generateSuratJalanWithLabelsPdf({
        suratJalanElement: null,
        sheetElements: activeSheetEls,
        poNumber,
        includeSuratJalan: false,
        includeLabels: true,
        onProgress: (cur, tot, msg) => setPdfProgressText(msg)
      });
      toast.success('PDF Lembar Label Dus berhasil diunduh!');
    } catch (err) {
      console.error('Gagal membuat PDF label dus:', err);
      toast.error('Gagal membuat PDF label dus.');
    } finally {
      setIsGeneratingPdf(false);
      setPdfProgressText('');
    }
  };

  // Direct Browser Native Vector Print (Tanpa html2canvas sehingga teks 100% rapi dan tajam)
  const handlePrint = () => {
    const origTitle = document.title;
    const cleanBrand = (order?.brand_name || 'Satala').split(' / ')[0].trim();
    const cleanPo = String(poNumber || 'PO').replace(/[/\\?%*:|"<>]/g, '-').trim();
    document.title = initialMode === 'both'
      ? `Surat-Jalan-dan-Label-Dus-${cleanPo}-${cleanBrand}`
      : `Label-Dus-${cleanPo}-${cleanBrand}`;

    try {
      window.print();
    } finally {
      setTimeout(() => {
        document.title = origTitle;
      }, 500);
      onClose();
    }
  };

  // Auto direct print handling (Direct to native browser print like SuratJalanPrint)
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHasAutoPrinted(false);
      return;
    }
    if (hasAutoPrinted) return;

    let isCancelled = false;

    // Listener browser saat dialog print ditutup atau selesai cetak/simpan PDF
    const handleAfterPrint = () => {
      onClose();
    };
    window.addEventListener('afterprint', handleAfterPrint);

    const executePrint = async () => {
      // Tunggu font Google Montserrat siap di memori browser agar preview print tidak lag/freeze me-resolve font
      try {
        if (typeof document !== 'undefined' && document.fonts) {
          await document.fonts.ready;
        }
      } catch (_) {}

      // Berikan jeda 1 tick render agar seluruh DOM SVG siap
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => setTimeout(r, 200));

      if (isCancelled) return;

      setHasAutoPrinted(true);
      handlePrint();
    };

    executePrint();

    return () => {
      isCancelled = true;
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [isOpen, hasAutoPrinted, onClose]);

  if (!isOpen) return null;

  // Direct print workflow (Tidak perlu lagi menampilkan modal besar Gambar 1)
  return (
    <>
      {/* Loading Modal Kompak Persis Gambar 2 */}
      <div className="fixed inset-0 z-[999999] bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 max-w-xs w-full text-center shadow-2xl border border-[#E6D5B8] animate-in zoom-in-95 duration-150">
          <div className="w-10 h-10 border-3 border-[#8C6208] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <h3 className="font-bold text-[#302621] text-sm">Membuka Dialog Print...</h3>
          <p className="text-xs text-slate-500 mt-1">
            {initialMode === 'both' ? 'Merapikan Surat Jalan & Label Dus untuk dicetak...' : 'Merapikan Lembar Label Dus untuk dicetak...'}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 px-4 py-1.5 bg-[#302621] hover:bg-black text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
          >
            Batalkan
          </button>
        </div>
      </div>

      {/* Portal Native Vector Multi-Halaman Langsung ke document.body */}
      {typeof document !== 'undefined' && createPortal(
        <div id="unified-print-container">
          {/* Global Shared SVG Defs: Logo SDE di-decode 1x saja oleh GPU browser untuk seluruh stiker */}
          <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <defs>
              <image
                id="satala-sd-logo-global"
                href={LOGO_HITAM_SDE_BASE64}
                xlinkHref={LOGO_HITAM_SDE_BASE64}
                width="38"
                height="42"
                preserveAspectRatio="xMidYMid meet"
              />
            </defs>
          </svg>

          {/* Halaman 1: Dokumen Surat Jalan Resmi Satala (A4 Portrait Asli) */}
          {(initialMode === 'both' || previewTab === 'all' || previewTab === 'sj') && (
            <div className="unified-print-page">
              <SuratJalanDocument
                order={order}
                shipment={shipment}
                companySettings={initialSettings.companySettings || {}}
              />
            </div>
          )}

          {/* Halaman 2+: Lembar Label Dus A4 Portrait (Rotasi 90 Derajat Menghadap Kanan) */}
          {(initialMode === 'both' || previewTab === 'all' || previewTab === 'labels') && (
            sheets.map((sheet) => (
              <div key={`portal-sheet-${sheet.sheetIndex}`} className="unified-print-page">
                <LabelDusSheetGrid
                  boxesList={sheet.boxes}
                  labelSize={labelSize}
                  brandName={brandName}
                  recipientName={recipientName}
                  recipientAddress={recipientAddress}
                  poNumber={poNumber}
                  variantName={selectedVariantName}
                  tglProduksi={formatDateIndo(tglProduksi)}
                  expDate={formatDateIndo(expDate)}
                  catatanDus={catatanDus}
                  qrTargetUrl={qrTargetUrl}
                  sheetIndex={sheet.sheetIndex}
                  totalSheets={sheets.length}
                  isPortraitMode={true}
                />
              </div>
            ))
          )}
        </div>,
        document.body
      )}

      {/* Global CSS untuk Print Multi-Halaman Native Browser */}
      <style>{`
        /* Stiker SVG Typography & Stroke Classes Global */
        .st-bold { font-family: 'Montserrat', Montserrat-Bold, -apple-system, sans-serif; font-weight: 700; fill: #231f20; }
        .st-white { font-family: 'Montserrat', Montserrat-Bold, -apple-system, sans-serif; font-weight: 700; fill: #ffffff !important; }
        .st-extrabold { font-family: 'Montserrat', Montserrat-ExtraBold, -apple-system, sans-serif; font-weight: 900; fill: #231f20; }
        .st-semibold { font-family: 'Montserrat', Montserrat-SemiBold, -apple-system, sans-serif; font-weight: 600; fill: #231f20; }
        .st-italic { font-family: 'Montserrat', Montserrat-ExtraBoldItalic, -apple-system, sans-serif; font-weight: 800; font-style: italic; fill: #231f20; }
        .st-line { stroke: #231f20; stroke-width: 0.5px; fill: none; }
        .st-dashed { stroke: #231f20; stroke-width: 0.5px; stroke-dasharray: 3, 2; fill: none; }

        @media screen {
          #unified-print-container {
            display: none !important;
          }
        }
        @media print {
          body > #root,
          [data-radix-portal],
          [role="dialog"] {
            display: none !important;
          }

          #unified-print-container {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }

          @page {
            size: A4 portrait !important;
            margin: 0 !important;
          }

          html, body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }

          .unified-print-page {
            width: 210mm !important;
            min-height: 297mm !important;
            height: 297mm !important;
            max-height: 297mm !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            box-sizing: border-box !important;
            margin: 0 auto !important;
            padding: 0 !important;
            background: #ffffff !important;
            overflow: hidden !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            contain: strict !important;
            will-change: transform !important;
          }
        }
      `}</style>
    </>
  );
}
